/**
 * Free gift auto-add — eligibility from Liquid promo-adjusted totals
 * (buy-2 campaigns / pick-10 / native discounts), never undiscounted origin sum.
 *
 * cart.js has no product tags, so JS reads [data-free-gift-state] from
 * section HTML that Liquid already computed with cart-promo-pricing.
 *
 * v2 (2026-08-25): The fetch wrapper now decides SYNCHRONOUSLY whether a
 * request is one it cares about (/cart/change on the cart page) and hands
 * everything else straight back to native fetch — same promise object,
 * untouched. The previous version awaited every fetch on the page inside
 * an async wrapper, which made free-gift-cart.js:245 the top stack frame
 * of every network failure sitewide (Shopify telemetry, beacons, pixels —
 * all fire-and-forget calls that reject under ad blockers). The errors
 * were never ours, but the attribution was, and it drowned Clarity and
 * the console in "script error." noise. Gift behaviour is unchanged.
 */
(() => {
  const PROPERTY_KEY = '_free_gift';
  const STATE_SELECTOR = '[data-free-gift-state]';
  const nativeFetch = window.fetch.bind(window);
  const root = () => window.Shopify?.routes?.root || '/';

  let intercepting = false;
  let cartChangeHookDepth = 0;
  let cachedSectionId = null;

  // Cart state Shopify already refused the gift for (usually no inventory left for the gift
  // variant). Retrying only makes sense once the cart holds a different amount of it.
  let giftBlockedForKey = null;

  function getConfig() {
    const config = window.themeVariables?.freeGift;
    if (!config?.variantId || !config?.thresholdCents) return null;
    return config;
  }

  function isCartPage() {
    return window.themeVariables?.settings?.pageType === 'cart';
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function isCartChangeRequest(input) {
    return requestUrl(input).includes('/cart/change');
  }

  /**
   * A gift line is identified by its _free_gift property, never by its variant: the gift
   * product can also be bought normally, and that purchase must not block the gift.
   */
  function giftInCart(cart) {
    const items = cart?.items || [];
    for (let i = 0; i < items.length; i++) {
      if (items[i]?.properties?.[PROPERTY_KEY]) return true;
    }
    return false;
  }

  /** How many units of the gift variant the cart holds, gift line included. */
  function giftVariantSignature(cart, variantId) {
    const id = Number(variantId);
    const items = cart?.items || [];
    let quantity = 0;

    for (let i = 0; i < items.length; i++) {
      if (Number(items[i].variant_id) === id) quantity += items[i].quantity;
    }
    return `${id}:${quantity}`;
  }

  function parseStateFromHtml(html) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(STATE_SELECTOR);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function parseStateFromCartSections(cart) {
    const sections = cart?.sections;
    if (!sections) return null;

    const values = Object.values(sections);
    for (let i = 0; i < values.length; i++) {
      const state = parseStateFromHtml(values[i]);
      if (state?.variantId) return state;
    }
    return null;
  }

  function readDomState() {
    const nodes = document.querySelectorAll(STATE_SELECTOR);
    if (!nodes.length) return null;

    let latest = null;
    for (let i = 0; i < nodes.length; i++) {
      try {
        latest = JSON.parse(nodes[i].textContent);
      } catch (error) {
        // ignore
      }
    }
    return latest;
  }

  function resolveSectionId() {
    if (cachedSectionId) return cachedSectionId;

    const el =
      document.querySelector('cart-drawer')?.closest('.shopify-section') ||
      document.querySelector('.shopify-section--main-cart');

    if (!el?.id) return null;
    cachedSectionId = el.id.replace(/^shopify-section-/, '');
    return cachedSectionId;
  }

  /** Liquid-rendered eligibility (discounted campaign totals). */
  async function resolveGiftState(cart) {
    const fromSections = parseStateFromCartSections(cart);
    if (fromSections) return fromSections;

    const fromDom = readDomState();
    // DOM may be stale right after a cart mutation — prefer a fresh section fetch.
    const sectionId = resolveSectionId();
    if (!sectionId) return fromDom;

    try {
      const response = await nativeFetch(`${root()}?section_id=${encodeURIComponent(sectionId)}`, {
        credentials: 'same-origin',
      });
      if (!response.ok) return fromDom;
      return parseStateFromHtml(await response.text()) || fromDom;
    } catch (error) {
      return fromDom;
    }
  }

  function shouldAddGift(state, cart, config) {
    if (!config || !cart) return false;
    if (giftInCart(cart)) return false;
    if (giftBlockedForKey && giftBlockedForKey === giftVariantSignature(cart, config.variantId)) return false;
    if (!state || state.eligible !== true) return false;
    return true;
  }

  function collectSectionIds(cart) {
    const fromCart = Object.keys(cart?.sections || {});
    if (fromCart.length) return fromCart;

    const bundled = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: bundled },
      })
    );
    return bundled;
  }

  async function fetchCart() {
    const response = await nativeFetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('cart.js failed');
    return response.json();
  }

  async function addFreeGift(variantId, sectionIds, cart) {
    const payload = {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: { [PROPERTY_KEY]: '1' },
        },
      ],
    };

    if (sectionIds?.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const response = await nativeFetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));

      // 422 means Shopify rejected the line itself — most often the gift variant has no
      // unit left because the cart already holds its whole available stock. Remember the
      // cart state so the gift is not requested again on every page view.
      if (response.status === 422) {
        giftBlockedForKey = giftVariantSignature(cart, variantId);
      }

      throw new Error(error.description || error.message || `free gift add failed (${response.status})`);
    }

    return response.json();
  }

  function dispatchCartChange(detail) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail,
      })
    );
  }

  async function replaceMainCartSection() {
    const sectionEl = document.querySelector('.shopify-section--main-cart');
    if (!sectionEl?.id) return false;

    const sectionId = sectionEl.id.replace(/^shopify-section-/, '');
    const response = await nativeFetch(`${root()}?section_id=${encodeURIComponent(sectionId)}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) return false;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById(`shopify-section-${sectionId}`);
    if (!fresh) return false;

    sectionEl.replaceWith(fresh);
    return true;
  }

  /**
   * Cart page qty change: theme reloads once.
   * Add gift first only when Liquid discounted total qualifies.
   *
   * The wrapper must stay invisible to every request it does not act on.
   * All gate checks that can run synchronously happen BEFORE any promise is
   * created, and the untouched native promise is returned for those calls:
   * their rejections then carry the caller's own stack, not ours. Only a
   * successful /cart/change on the cart page enters the async path.
   */
  window.fetch = function patchedFetch(input, init) {
    if (
      cartChangeHookDepth > 0 ||
      intercepting ||
      !isCartPage() ||
      !isCartChangeRequest(input)
    ) {
      return nativeFetch(input, init);
    }

    const config = getConfig();
    if (!config) return nativeFetch(input, init);

    return (async () => {
      const response = await nativeFetch(input, init);
      if (!response.ok) return response;

      cartChangeHookDepth += 1;
      try {
        const cart = await response.clone().json();
        const state = await resolveGiftState(cart);
        if (shouldAddGift(state, cart, config)) {
          await addFreeGift(config.variantId, null, cart);
        }
      } catch (error) {
        console.warn('[free-gift] gift not added:', error.message || error);
      } finally {
        cartChangeHookDepth -= 1;
      }

      return response;
    })();
  };

  /**
   * Drawer / AJAX add: use Liquid eligibility from returned sections.
   */
  document.addEventListener(
    'cart:change',
    (event) => {
      if (intercepting || event.detail?.freeGiftHandled || isCartPage()) return;

      const config = getConfig();
      const cart = event.detail?.cart;
      if (!config || !cart) return;

      // Sync decision — stop only when we know we must add the gift.
      // Resolve state async; stopImmediatePropagation must run before await.
      const syncedState = parseStateFromCartSections(cart);
      if (syncedState) {
        if (!shouldAddGift(syncedState, cart, config)) return;

        event.stopImmediatePropagation();
        intercepting = true;
        const originalDetail = event.detail;

        (async () => {
          try {
            const sectionIds = collectSectionIds(cart);
            const addResponse = await addFreeGift(config.variantId, sectionIds, cart);
            const updatedCart = await fetchCart();
            updatedCart.sections = addResponse.sections || cart.sections || {};

            dispatchCartChange({
              ...originalDetail,
              cart: updatedCart,
              freeGiftHandled: true,
            });
          } catch (error) {
            console.warn('[free-gift] gift not added:', error.message || error);
            dispatchCartChange({
              ...originalDetail,
              freeGiftHandled: true,
            });
          } finally {
            intercepting = false;
          }
        })();
        return;
      }

      // Sections missing state — fetch Liquid eligibility, then add if needed.
      event.stopImmediatePropagation();
      intercepting = true;
      const originalDetail = event.detail;

      (async () => {
        try {
          const state = await resolveGiftState(cart);
          if (!shouldAddGift(state, cart, config)) {
            dispatchCartChange({
              ...originalDetail,
              freeGiftHandled: true,
            });
            return;
          }

          const sectionIds = collectSectionIds(cart);
          const addResponse = await addFreeGift(config.variantId, sectionIds, cart);
          const updatedCart = await fetchCart();
          updatedCart.sections = addResponse.sections || cart.sections || {};

          dispatchCartChange({
            ...originalDetail,
            cart: updatedCart,
            freeGiftHandled: true,
          });
        } catch (error) {
          console.warn('[free-gift] gift not added:', error.message || error);
          dispatchCartChange({
            ...originalDetail,
            freeGiftHandled: true,
          });
        } finally {
          intercepting = false;
        }
      })();
    },
    true
  );

  /**
   * Page load: only add when Liquid discounted total qualifies.
   * Soft UI refresh — no extra full reload.
   */
  async function syncOnLoad() {
    const config = getConfig();
    if (!config || intercepting) return;

    try {
      const cart = await fetchCart();
      const state = await resolveGiftState(cart);
      if (!shouldAddGift(state, cart, config)) return;

      intercepting = true;

      if (isCartPage()) {
        await addFreeGift(config.variantId, null, cart);
        await replaceMainCartSection();
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      const sectionIds = collectSectionIds(cart);
      const addResponse = await addFreeGift(config.variantId, sectionIds, cart);
      const updatedCart = await fetchCart();
      updatedCart.sections = addResponse.sections || {};

      if (Object.keys(updatedCart.sections).length) {
        dispatchCartChange({
          baseEvent: 'free-gift:add',
          cart: updatedCart,
          freeGiftHandled: true,
        });
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      console.warn('[free-gift] gift not added:', error.message || error);
    } finally {
      intercepting = false;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncOnLoad, { once: true });
  } else {
    syncOnLoad();
  }
})();